import React, { FC, forwardRef, useCallback, useState } from 'react';
import { IoMove } from 'react-icons/io5';

import { UseDraggableArguments, useDraggable } from '@dnd-kit/core';
import { ActionIcon, Box, Stack, Text, UnstyledButton } from '@mantine/core';
import { useElementSize, useResizeObserver } from '@mantine/hooks';

import { ComponentType } from 'models';
import { getTouchDeviceType, TouchDeviceType } from 'helpers/isTouchDevice';

import { ComponentIcon } from 'components/component-icons/ComponentIcon';

import cx from './CategoryGridItem.module.scss';

type CategoryGridItemProps = {
    type: ComponentType;
    custom?: string | null;
    label?: string;
    subtitle?: string;
    image?: React.ReactNode;
    withDragHandle?: boolean;
};

const CategoryGridItemContent: FC<CategoryGridItemProps> = ({ type, custom, label, subtitle, image }) => {
    const hasLabel = Boolean(label || subtitle);
    const iconSize = image ? '45%' : hasLabel ? '25%' : '40%';

    return (
        <>
            <div style={{ width: iconSize, height: iconSize }}>
                {image ?? <ComponentIcon type={type} custom={custom} />}
            </div>
            {hasLabel && (
                <Stack gap={4}>
                    {label && (
                        <Text size="xs" c="gray.9" lineClamp={2} style={{ textAlign: 'center' }}>
                            {label}
                        </Text>
                    )}

                    {subtitle && (
                        <Text size="xs" c="dimmed" lineClamp={1} style={{ textAlign: 'center' }}>
                            {subtitle}
                        </Text>
                    )}
                </Stack>
            )}
        </>
    );
};

const CategoryGridItem = forwardRef<
    HTMLDivElement,
    CategoryGridItemProps & {
        children?: React.ReactNode;
    }
>(({ children, ...props }, ref) => {
    return (
        <Box className={cx.item} ref={ref}>
            <CategoryGridItemContent {...props} />
            {children}
        </Box>
    );
});
CategoryGridItem.displayName = 'CategoryGridItem';

const CategoryGridButton: FC<CategoryGridItemProps & { onClick: () => void; selected?: boolean }> = ({
    onClick,
    selected = false,
    ...props
}) => {
    return (
        <UnstyledButton
            className={[cx.item, cx.hover, selected ? cx.selected : null].filter(Boolean).join(' ')}
            onClick={onClick}
        >
            <CategoryGridItemContent {...props} />
        </UnstyledButton>
    );
};

const DraggableCategoryGridItem: FC<
    CategoryGridItemProps & {
        dragOptions: UseDraggableArguments;
    }
> = ({ type, label, subtitle, image, withDragHandle = true, dragOptions }) => {
    const touchDeviceType = getTouchDeviceType();
    const showDragHandle = withDragHandle && touchDeviceType !== TouchDeviceType.NOT_TOUCH_DEVICE;
    const isTabletDevice = touchDeviceType === TouchDeviceType.TABLET;

    const { ref: sizeRef, width, height } = useElementSize();
    const [wrapper, setwrapper] = useState<HTMLDivElement | null>(null);

    const wrapperRef = useCallback(
        (node: HTMLDivElement | null) => {
            sizeRef(node);
            setwrapper(node);
        },
        [sizeRef],
    );

    const { attributes, listeners, setNodeRef, transform, setActivatorNodeRef } = useDraggable(dragOptions);

    const wrapperBoundingClientRect = wrapper?.getBoundingClientRect();

    return (
        <CategoryGridItem type={type} label={label} subtitle={subtitle} image={image} ref={wrapperRef}>
            <UnstyledButton
                component="div"
                className={cx.item}
                style={{
                    position: transform ? 'fixed' : 'absolute',
                    left: transform && wrapperBoundingClientRect ? wrapperBoundingClientRect.left - 1 : undefined,
                    top: transform && wrapperBoundingClientRect ? wrapperBoundingClientRect.top - 1 : undefined,

                    zIndex: transform ? 9999 : undefined,

                    display: width && height ? 'flex' : 'none',

                    width: width + 2, // 2 = border width
                    height: height + 2, // 2 = border width

                    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : 'none',
                }}
                data-interactive={!isTabletDevice}
                {...attributes}
                {...(isTabletDevice ? {} : listeners)}
                ref={setNodeRef}
            >
                {showDragHandle && (
                    <ActionIcon
                        className={cx.dragHandle}
                        variant="light"
                        bg="gray.0"
                        c="dimmed"
                        size="lg"
                        ref={setActivatorNodeRef}
                        {...(isTabletDevice ? listeners : {})}
                    >
                        <IoMove />
                    </ActionIcon>
                )}

                <CategoryGridItemContent type={type} label={label} subtitle={subtitle} image={image} />
            </UnstyledButton>
        </CategoryGridItem>
    );
};

export { CategoryGridButton, CategoryGridItem, DraggableCategoryGridItem };
